import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { SpinResponse } from '@cgs/data-access';
import {
  CollapseGameStateMachine,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
} from '@cgs/machines';
import { TRIFECTA_LAUNCH_PARAMS_TOKEN, TrifectaLaunchParamsDto } from '@cgs/provider-trifecta';
import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  SceneObject,
  SequenceAction,
  TextSceneObject,
} from '@cgs/wge';

export const SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN = Symbol('SantaFreeSpinsTotalWinComponent');

const enum SceneNames {
  TotalWinPanel = 'FreeSpinTotalWinPanel',
  TotalWinText = 'TotalWinText',
}

const enum SceneStates {
  Show = 'show',
  Static = 'static',
  Anim = 'anim',
  Hide = 'hide',
}

export class SantaFreeSpinsTotalWinComponent {
  private readonly totalWinPanel: SceneObject;
  private readonly totalWinText: TextSceneObject;
  private readonly gameStateMachine: CollapseGameStateMachine<SpinResponse>;
  private readonly numberFormatter: INumberFormatter;
  private totalWin: number = 0;

  private readonly currency: string;
  private readonly currencyDecimals: number;

  constructor(private readonly container: Container) {
    const resourcesComponent =
      this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    const launchParams = this.container.resolve<TrifectaLaunchParamsDto>(
      TRIFECTA_LAUNCH_PARAMS_TOKEN
    );
    this.currency = launchParams.currencyId;
    this.currencyDecimals = launchParams.currencyDecimals;

    const totalWinPanel = resourcesComponent.slot.findById(SceneNames.TotalWinPanel);

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as CollapseGameStateMachine<SpinResponse>;

    if (!totalWinPanel) {
      throw new Error('Total win panel not found');
    }

    const totalWinText = totalWinPanel.findById<TextSceneObject>(SceneNames.TotalWinText);
    if (!totalWinText) {
      throw new Error('Total win text not found');
    }

    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);

    this.totalWinText = totalWinText;

    this.totalWinPanel = totalWinPanel;

    this.gameStateMachine.animation.appendLazyAnimation(() => this.showTotalWinAnimationAction());

    this.gameStateMachine.freeSpinsRecovery.appendLazyAnimation(() =>
      this.freeSpinsRecoveryAction()
    );
    this.gameStateMachine.beginFreeSpinsPopup.appendLazyAnimation(() =>
      this.showFreeSpinsPopupAction()
    );
    this.gameStateMachine.endFreeSpinsPopup.appendLazyAnimation(() =>
      this.endFreeSpinsPopupAction()
    );
  }

  public showFreeSpinsTotalWinAction(totalWin: number): IntervalAction {
    const isFreeSpins = this.gameStateMachine.curResponse.isFreeSpins;

    if (!isFreeSpins) {
      return new EmptyAction();
    }

    if (totalWin === 0) {
      return new EmptyAction();
    }

    this.totalWin += totalWin;

    return new SequenceAction([
      new EmptyAction().withDuration(
        this.totalWinPanel.stateMachine!.getStateDuration(SceneStates.Anim) * 0.5
      ),
      new FunctionAction(() => {
        this.totalWinPanel.stateMachine?.switchToState(SceneStates.Static);
        this.totalWinPanel.stateMachine?.switchToState(SceneStates.Anim);
      }),
      new EmptyAction().withDuration(
        this.totalWinPanel.stateMachine!.getStateDuration(SceneStates.Anim) * 0.5
      ),
      new FunctionAction(() => {
        this.totalWinText.text = `${this.currency} ${this.numberFormatter.format(this.totalWin, this.currencyDecimals)}`;
      }),
    ]);
  }

  private showTotalWinAnimationAction(): IntervalAction {
    const isFreeSpins = this.gameStateMachine.curResponse.isFreeSpins;

    if (!isFreeSpins) {
      return new EmptyAction();
    }
    const totalWin = this.gameStateMachine.curResponse.freeSpinsInfo!.totalWin;

    this.totalWin = totalWin;
    return new FunctionAction(() => {
      this.totalWinText.text = `${this.currency} ${this.numberFormatter.format(this.totalWin, this.currencyDecimals)}`;
    });
  }

  private freeSpinsRecoveryAction(): IntervalAction {
    const win =
      this.gameStateMachine.curResponse.freeSpinsInfo!.totalWin -
      this.gameStateMachine.curResponse.totalWin;
    this.totalWin = win;

    return new FunctionAction(() => {
      this.totalWinPanel.stateMachine?.switchToState(SceneStates.Show);
      this.totalWinText.text = `${this.currency} ${this.numberFormatter.format(this.totalWin, this.currencyDecimals)}`;
    });
  }

  private endFreeSpinsPopupAction(): IntervalAction {
    return new FunctionAction(() => {
      this.totalWinPanel.stateMachine?.switchToState(SceneStates.Hide);
    });
  }
  private showFreeSpinsPopupAction(): IntervalAction {
    return new FunctionAction(() => {
      this.totalWinPanel.stateMachine?.switchToState(SceneStates.Show);
    });
  }
}
